"use client";
import React, { ReactNode, useEffect, useState } from "react";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
// Persistence temporarily disabled until packages are aligned
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { MuiProviders } from "@packages/ui/mui";
import { ToastContainer } from "@packages/ui/libs";
import { SessionProvider } from "next-auth/react";
import { UserProvider } from "./context";
import ConfigProvider from "./ConfigProvider";
import { NetworkProvider } from "./NetworkProvider";
import ErrorBoundary from "@libs/helpers/errorBoundary";
import { useNavigationCleanup } from "@libs/customHooks/useNavigationCleanup";

interface props {
  children: ReactNode;
  session: any;
}

// Navigation cleanup component that must be inside QueryClientProvider
function NavigationCleanupWrapper() {
  useNavigationCleanup();
  return null;
}

const Providers = ({ children, session }: props) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Keep data fresh for 5 minutes
            staleTime: 5 * 60 * 1000,
            // Cache data for 24 hours
            gcTime: 24 * 60 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            refetchOnMount: true,
            retry: (failureCount, error) => {
              // Don't retry on network errors
              if (
                error?.message?.includes("Network is offline") ||
                error?.message?.includes("Network connection lost") ||
                ((error as any)?.name === "TypeError" &&
                  error?.message === "Failed to fetch")
              ) {
                return false;
              }
              return failureCount < 2;
            },
            networkMode: "online",
            // Don't throw errors to prevent breaking the app
            throwOnError: false,
          },
          mutations: {
            networkMode: "online",
            retry: (failureCount, error) => {
              // Don't retry mutations on network errors
              if (
                error?.message?.includes("Network is offline") ||
                error?.message?.includes("Network connection lost") ||
                ((error as any)?.name === "TypeError" &&
                  error?.message === "Failed to fetch")
              ) {
                return false;
              }
              return failureCount < 1;
            },
          },
        },
      })
  );

  useEffect(() => {
    const handleOnline = () => {
      console.log(
        "Application is back online. Resuming and refetching active queries..."
      );
      // Resume paused mutations and refetch ALL queries to ensure everything is up to date
      queryClient.resumePausedMutations();
      queryClient.refetchQueries({
        type: "all",
        predicate: (query) => {
          // Refetch all queries that might have failed or need fresh data
          return true; // Refetch everything to ensure consistency
        },
      });
    };

    const handleOffline = () => {
      console.log("Application is offline.");
      // Cancel all ongoing queries to prevent unnecessary network requests
      queryClient.cancelQueries();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [queryClient]);

  // Cross-tab role change listener: reload app when role is changed in another tab
  useEffect(() => {
    if (typeof window === "undefined") return;

    const STORAGE_KEY = "vericap_role_change";

    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        try {
          // Navigate to dashboard to ensure the new role and permissions are applied
          window.location.assign("/dashboard");
        } catch (err) {
          console.error(
            "Error navigating to /dashboard after role change:",
            err
          );
        }
      }
    };

    // BroadcastChannel support for modern browsers
    let bc: BroadcastChannel | null = null;
    try {
      if ((window as any).BroadcastChannel) {
        bc = new BroadcastChannel("vericap_role_change");
        bc.onmessage = () => {
          try {
            window.location.assign("/dashboard");
          } catch (err) {
            console.error(
              "Error navigating to /dashboard after broadcast role change:",
              err
            );
          }
        };
      }
    } catch (err) {
      // ignore
    }

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
      try {
        if (bc) {
          bc.close();
          bc = null;
        }
      } catch (err) {
        /* ignore */
      }
    };
  }, []);

  return (
    <ErrorBoundary>
      <SessionProvider session={session}>
        <QueryClientProvider client={queryClient}>
          <NetworkProvider>
          <NavigationCleanupWrapper />
          <ReactQueryDevtools initialIsOpen={false} />
            <MuiProviders>
              <div>
                <UserProvider>
                  <ConfigProvider>{children}</ConfigProvider>
                </UserProvider>
                <ToastContainer limit={3} />
              </div>
            </MuiProviders>
          </NetworkProvider>
        </QueryClientProvider>
      </SessionProvider>
    </ErrorBoundary>
  );
};

export default Providers;
